import {Button, Dialog, Spinner, Stack, Text} from '@primer/react'
import {useCallback, useRef, type RefObject} from 'react'

import styles from './DeleteConversationDialog.module.css'

export interface DeleteConversationDialogProps {
  isPending: boolean
  error: string | null
  onConfirm: () => void
  onClose: () => void
  title?: string
  message?: string
  confirmLabel?: string
  busyLabel?: string
  returnFocusRef?: RefObject<HTMLElement | null>
}

export function DeleteConversationDialog({
  isPending,
  error,
  onConfirm,
  onClose,
  title = 'Delete conversation',
  message = 'Are you sure you want to delete this conversation? This action cannot be undone.',
  confirmLabel = 'Delete',
  busyLabel = 'Deleting conversation',
  returnFocusRef,
}: DeleteConversationDialogProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null)
  const handleConfirm = useCallback(() => {
    if (isPending) return
    onConfirm()
  }, [isPending, onConfirm])

  return (
    <Dialog
      title={title}
      onClose={onClose}
      width="medium"
      returnFocusRef={returnFocusRef}
      initialFocusRef={cancelButtonRef}
    >
      <Stack direction="vertical" gap="normal">
        <Text as="p" className={styles.Message}>
          {message}
        </Text>
        <Stack direction="horizontal" gap="condensed" align="center" justify="space-between">
          <div role="status">
            {error && (
              <Text as="p" className={styles.ErrorText}>
                {error}
              </Text>
            )}
            {isPending && <Spinner size="small" srText={busyLabel} />}
          </div>
          <Stack direction="horizontal" gap="condensed" align="center" justify="end">
            <Button
              type="button"
              ref={cancelButtonRef}
              onClick={onClose}
              data-testid="delete-conversation-dialog-dismiss"
            >
              {isPending ? 'Close' : 'Cancel'}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleConfirm}
              aria-disabled={isPending}
              inactive={isPending}
            >
              {confirmLabel}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Dialog>
  )
}
